// Agrégation d'un flux CliEvent en blocs rendus (vue Messages).
import type { CliEvent, MsgBlock, TodoWriteItem } from "./types";

// Liste des outils dont l'invocation nécessite une confirmation opérateur.
// Source de vérité = relais (DESTRUCTIVE_TOOLS) ; cette liste sert au rendu UI.
export function toolUseNeedsApproval(ev: CliEvent): boolean {
  // Heuristique Phase A : un tool_use marqué pendingApproval par l'adapter.
  const ti = ev.toolInput as { pendingApproval?: boolean } | null | undefined;
  return Boolean(ti && ti.pendingApproval);
}

/**
 * Récupère le dernier état TodoWrite d'un flux de CliEvent (Claude Code).
 * Claude met à jour son plan au fil de l'exécution : on garde le DERNIER
 * `tool_use { toolName: "TodoWrite" }` (l'état courant du plan vivant).
 *
 * Ici (et pas dans custom-tabs.tsx) pour rester testable sans dépendance UI —
 * ce module n'importe aucun composant SolidJS.
 */
export function extractLastTodoWrite(events: CliEvent[]): TodoWriteItem[] | null {
  let last: TodoWriteItem[] | null = null;
  for (const ev of events) {
    if (ev.kind !== "tool_use" || ev.toolName !== "TodoWrite") continue;
    const input = ev.toolInput as { todos?: TodoWriteItem[] } | null | undefined;
    if (input && Array.isArray(input.todos)) last = input.todos;
  }
  return last;
}

/**
 * Agrège un flux de CliEvent en blocs rendus :
 * - les `assistant_delta` consécutifs sont fusionnés dans le bloc assistant courant ;
 * - `tool_result` est corrélé au `tool_use` précédent par `nativeId` (sinon dernier ouvert).
 */
export function aggregate(events: CliEvent[]): MsgBlock[] {
  const blocks: MsgBlock[] = [];
  let assistantBuf: { id: string; content: string; at: string } | null = null;
  const openTools = new Map<string, MsgBlock & { kind: "tool" }>();
  let lastTool: (MsgBlock & { kind: "tool" }) | null = null;

  const flushAssistant = () => {
    if (assistantBuf && assistantBuf.content.length > 0) {
      blocks.push({ kind: "assistant", ...assistantBuf });
    }
    assistantBuf = null;
  };

  for (let i = 0; i < events.length; i++) {
    const ev = events[i];
    const id = `${ev.seq}_${i}`;
    const at = ev.atUtc;
    switch (ev.kind) {
      case "user_msg":
        flushAssistant();
        blocks.push({ kind: "user", id, content: ev.content || "", at });
        break;
      case "assistant_msg":
        flushAssistant();
        blocks.push({ kind: "assistant", id, content: ev.content || "", at });
        break;
      case "assistant_delta":
        if (!assistantBuf) assistantBuf = { id, content: "", at };
        assistantBuf.content += ev.content || "";
        break;
      case "thinking":
        flushAssistant();
        blocks.push({ kind: "thinking", id, content: ev.content || "", at });
        break;
      case "status":
        flushAssistant();
        blocks.push({ kind: "status", id, content: ev.content || "", at });
        break;
      case "error":
        flushAssistant();
        blocks.push({ kind: "error", id, content: ev.content || "", at });
        break;
      case "tool_use": {
        flushAssistant();
        const block: MsgBlock & { kind: "tool" } = {
          kind: "tool",
          id,
          toolName: ev.toolName || "(outil)",
          toolInput: ev.toolInput ?? null,
          toolResult: null,
          nativeId: ev.nativeId ?? null,
          needsApproval: toolUseNeedsApproval(ev),
          at,
        };
        blocks.push(block);
        if (ev.nativeId) openTools.set(ev.nativeId, block);
        lastTool = block;
        break;
      }
      case "tool_result": {
        const target = (ev.nativeId && openTools.get(ev.nativeId)) || lastTool;
        if (target) {
          target.toolResult = ev.toolResult ?? ev.content ?? null;
          if (ev.nativeId) openTools.delete(ev.nativeId);
        }
        break;
      }
      // raw / exit non rendus en vue MESSAGES
      default:
        break;
    }
  }
  flushAssistant();
  return blocks;
}
